// src/pages/Dashboard.js
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Users2, Server, Globe, AlertTriangle, Activity, Clock, Cpu, HardDrive, ArrowDown, ArrowUp, Wifi, Monitor, RefreshCw } from 'lucide-react';
import api from '../utils/api';
import { formatDistanceToNow } from 'date-fns';
import { ro } from 'date-fns/locale';

function StatCard({ icon: Icon, value, label, color, sub }) {
  return (
    <div className="stat-card fade-in">
      <div className="stat-icon" style={{ background: `${color}20` }}>
        <Icon size={20} color={color} />
      </div>
      <div className="stat-value" style={{ color }}>{value ?? '–'}</div>
      <div className="stat-label">{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function ExpirationBadge({ date }) {
  if (!date) return null;
  const days = Math.ceil((new Date(date) - new Date()) / 86400000);
  const cls = days <= 7 ? 'exp-critical' : days <= 30 ? 'exp-warning' : 'exp-ok';
  return <span className={cls} style={{ fontSize: 12, fontWeight: 600 }}>{days <= 0 ? 'Expirat!' : `${days}z`}</span>;
}

function formatBytes(value) {
  const bytes = Number(value) || 0;
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let size = bytes / 1024; let unit = units[0];
  for (let i = 1; size >= 1024 && i < units.length; i += 1) { size /= 1024; unit = units[i]; }
  return `${size.toFixed(size >= 10 ? 0 : 1)} ${unit}`;
}

function Sparkline({ values, color }) {
  if (!values?.length) return null;
  const max = Math.max(...values, 1); const min = Math.min(...values, 0); const spread = max - min || 1;
  const points = values.map((value, index) => `${(index / Math.max(values.length - 1, 1)) * 100},${27 - ((value - min) / spread) * 23}`).join(' ');
  return <svg viewBox="0 0 100 30" preserveAspectRatio="none" style={{ width: 86, height: 27, opacity: 0.9 }} aria-hidden="true"><polyline points={points} fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

function ResourceCard({ icon: Icon, title, color, value, detail, percent, trend, footer }) {
  const fill = Math.min(Math.max(Number(percent) || 0, 0), 100);
  return (
    <div className="server-resource-card" style={{ '--resource-color': color }}>
      <div className="server-resource-head"><span className="server-resource-icon"><Icon size={18} /></span><span>{title}</span><Sparkline values={trend} color={color} /></div>
      <div className="server-resource-value">{value}</div>
      <div className="server-resource-detail">{detail}</div>
      {percent !== undefined && <div className="server-resource-track"><span style={{ width: `${fill}%` }} /></div>}
      {footer && <div className="server-resource-footer">{footer}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [server, setServer] = useState(null);
  const [serverHistory, setServerHistory] = useState([]);
  const [serverError, setServerError] = useState(false);
  const [loading, setLoading] = useState(true);
  const polling = useRef(false);

  useEffect(() => {
    api.get('/dashboard').then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let active = true;
    const refresh = async () => {
      if (polling.current) return;
      polling.current = true;
      try {
        const { data: current } = await api.get('/dashboard/server');
        if (!active) return;
        setServer(current);
        setServerError(false);
        setServerHistory(previous => [...previous, {
          cpu: current.cpu.usagePercent ?? 0,
          memory: current.memory.usagePercent,
          network: current.network.rxBytesPerSecond + current.network.txBytesPerSecond,
        }].slice(-24));
      } catch { if (active) setServerError(true); }
      finally { polling.current = false; }
    };
    refresh();
    const timer = setInterval(refresh, 5000);
    return () => { active = false; clearInterval(timer); };
  }, []);

  if (loading) return <div className="loading-center"><span className="spinner" /></div>;
  if (!data) return null;

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <div className="page-title">Dashboard</div>
          <div className="page-subtitle">Bun venit înapoi! Iată o privire de ansamblu.</div>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          <Clock size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          {new Date().toLocaleDateString('ro-RO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: 24 }}>
        <StatCard icon={Users2} value={data.clients} label="Total Clienți" color="var(--accent)" sub={`${data.activeClients} activi`} />
        <StatCard icon={Server} value={data.vps} label="VPS Active" color="var(--success)" sub={`${data.monthlyCost?.toFixed(2)} EUR/lună`} />
        <StatCard icon={Globe} value={data.domains} label="Domenii Active" color="var(--warning)" />
        <StatCard icon={AlertTriangle} value={data.expiring30} label="Expiră în 30 zile" color="var(--danger)" sub={`${data.expiring7} în 7 zile`} />
      </div>

      <section className="server-dashboard-section">
        <div className="server-section-heading">
          <div><div className="server-section-title"><Monitor size={18} /> Server actual</div><div className="server-section-subtitle">Resurse și găzduire actualizate automat la fiecare 5 secunde</div></div>
          <div className={`server-live-badge ${serverError ? 'is-error' : ''}`}><span />{serverError ? 'Date indisponibile' : 'Monitorizare live'}{server?.capturedAt && <time>{new Date(server.capturedAt).toLocaleTimeString('ro-RO')}</time>}<RefreshCw size={13} className={serverError ? '' : 'server-refresh-icon'} /></div>
        </div>

        {server ? <>
          <div className="server-resource-grid">
            <ResourceCard icon={Cpu} title="Procesor" color="#4f8ef7" value={server.cpu.usagePercent == null ? 'Măsurare…' : `${server.cpu.usagePercent}%`} detail={`${server.host.cores} vCPU · load ${server.cpu.loadAverage.map(n => n.toFixed(2)).join(' / ')}`} percent={server.cpu.usagePercent ?? 0} trend={serverHistory.map(item => item.cpu)} footer="Utilizare CPU" />
            <ResourceCard icon={Activity} title="Memorie RAM" color="#a855f7" value={`${formatBytes(server.memory.usedBytes)} / ${formatBytes(server.memory.totalBytes)}`} detail={`${formatBytes(server.memory.availableBytes)} disponibili · swap ${formatBytes(server.memory.swapUsedBytes)} / ${formatBytes(server.memory.swapTotalBytes)}`} percent={server.memory.usagePercent} trend={serverHistory.map(item => item.memory)} footer={`${server.memory.usagePercent}% utilizat`} />
            <ResourceCard icon={HardDrive} title="Disc principal" color="#f59e0b" value={server.disk ? `${formatBytes(server.disk.usedBytes)} / ${formatBytes(server.disk.totalBytes)}` : 'Indisponibil'} detail={server.disk ? `${formatBytes(server.disk.availableBytes)} liberi · ${server.disk.mount}` : 'Nu am putut citi spațiul de stocare'} percent={server.disk?.usagePercent ?? 0} footer={`${server.disk?.usagePercent ?? 0}% ocupat`} />
            <ResourceCard icon={Wifi} title="Trafic rețea" color="#06b6d4" value={`${formatBytes(server.network.rxBytesPerSecond)}/s`} detail={`↑ ${formatBytes(server.network.txBytesPerSecond)}/s · interfață ${server.network.interface || '—'}`} trend={serverHistory.map(item => item.network)} footer={<span><ArrowDown size={12} /> Recepționat · <ArrowUp size={12} /> Trimis</span>} />
          </div>

          <div className="server-info-grid">
            <div className="server-info-card">
              <div className="server-info-card-title"><Server size={16} color="#4f8ef7" /> Informații server</div>
              <div className="server-detail-grid">
                <div><small>Hostname</small><strong>{server.host.hostname}</strong></div>
                <div><small>Sistem de operare</small><strong>{server.host.os}</strong></div>
                <div><small>Kernel</small><strong>{server.host.kernel}</strong></div>
                <div><small>Arhitectură</small><strong>{server.host.architecture}</strong></div>
                <div><small>Uptime</small><strong>{server.host.uptime}</strong></div>
                <div><small>Adrese IPv4</small><strong>{server.host.ipv4.map(address => address.address).join(', ') || '—'}</strong></div>
              </div>
            </div>
            <div className="server-info-card hosting-profile-card">
              <div className="server-info-card-title"><Globe size={16} color="#22c55e" /> Domenii găzduite pe server <b>{server.hosting.domainCount}</b></div>
              <div className="hosting-profiles">
                {server.hosting.profiles.map(profile => <div className="hosting-profile" key={profile.id}>
                  <div><strong>{profile.name}</strong><small>{profile.installed ? `${profile.domainCount} domenii · ${profile.source}` : 'Profil pregătit · panou nedetectat'}</small></div>
                  <span className={`hosting-panel-status ${profile.installed ? 'installed' : ''}`}>{profile.active ? 'Sursă activă' : profile.installed ? 'Instalat' : 'Nedetectat'}</span>
                </div>)}
              </div>
              <div className="hosted-domains-list">
                {server.hosting.domains.length ? server.hosting.domains.map(domain => <span key={`${domain.name}-${domain.owner || ''}`} title={domain.path || domain.owner || ''}>{domain.name}</span>) : <span className="hosted-domains-empty">Nu au fost găsite domenii în panoul activ.</span>}
              </div>
            </div>
          </div>
        </> : <div className="server-info-card server-loading-card"><span className="spinner" /> Se colectează informațiile serverului…</div>}
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Upcoming expirations */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700 }}>Expirări Apropiate</h3>
            <Link to="/hosting" style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'none' }}>Vezi toate →</Link>
          </div>
          {data.upcomingExp?.length === 0 ? (
            <div style={{ color: 'var(--text-secondary)', fontSize: 13, textAlign: 'center', padding: 20 }}>Nicio expirare în 30 de zile 🎉</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.upcomingExp.map((e, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--bg-hover)', borderRadius: 8 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{e.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{e.company_name} · {e.type.toUpperCase()}</div>
                  </div>
                  <ExpirationBadge date={e.expiry_date} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent activity */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <Activity size={16} color="var(--accent)" />
            <h3 style={{ fontSize: 15, fontWeight: 700 }}>Activitate Recentă</h3>
          </div>
          {data.recentActivity?.length === 0 ? (
            <div style={{ color: 'var(--text-secondary)', fontSize: 13, textAlign: 'center', padding: 20 }}>Nicio activitate</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {data.recentActivity.slice(0, 8).map((a, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)44' }}>
                  <div style={{ width: 28, height: 28, background: 'var(--accent)20', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 11, fontWeight: 700, color: 'var(--accent)' }}>
                    {a.full_name?.[0] || '?'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500 }}>{a.details}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                      {a.full_name} · {a.created_at ? formatDistanceToNow(new Date(a.created_at), { locale: ro, addSuffix: true }) : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
